import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import SaveIcon from "@mui/icons-material/Save";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  useTheme,
  Pagination,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState, ChangeEvent } from "react";

import { useHandleClients } from "0.Mine/1.Hooks/CRM/client";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";

// Type for a single note
export interface Note {
  id: string;
  title: string;
  description: string;
  createdAt: string;
  createdBy: string;
}

interface ClientNotesModalProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
}

const PAGE_SIZE = 5;

// Simple semantic icon mapping based on keywords
const getIconForNote = (note: Note) => {
  const text = `${note.title} ${note.description}`.toLowerCase();
  if (text.includes("warn")) return <WarningIcon />;
  if (text.includes("success") || text.includes("done")) return <CheckCircleIcon />;
  return <InfoIcon />;
};

const ClientNotesModal: React.FC<ClientNotesModalProps> = ({ open, onClose, clientId }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [notes, setNotes] = useState<Note[]>([]);
  const [page, setPage] = useState(1);
  const [newNote, setNewNote] = useState<{ title: string; description: string }>({
    title: "",
    description: "",
  });
  const { addClientNote, getClientNotes, editClientNotes, deleteClientNote } = useHandleClients();
  const { session } = useBusinessContext();
  // Fetch notes when modal opens
  useEffect(() => {
    if (!open) return;
    const fetchNotes = async () => {
      try {
        if (session && session.selectedBusiness) {
          const response = await getClientNotes(session.selectedBusiness.id, clientId);
          if (response) {
            setNotes(response);
          } else {
            console.error("Error fetching notes");
            setNotes([]);
          }
        }
      } catch (error) {
        setNotes([]);
        enqueueSnackbar("Errore nel recupero delle note", { variant: "error" });
      }
    };
    fetchNotes();
  }, [open, clientId]);

  // Handle pagination change
  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Handle inline note edits
  const handleEdit = (id: string, field: keyof Note) => (e: ChangeEvent<HTMLInputElement>) => {
    setNotes((prev) =>
      prev.map((note) => (note.id === id ? { ...note, [field]: e.target.value } : note))
    );
    try {
      const updatedNote = notes.find((note) => note.id === id);
      if (updatedNote) {
        editClientNotes(session.selectedBusiness.id, clientId, updatedNote);
      }
    } catch (error) {
      console.error("Error editing note:", error);
      enqueueSnackbar("Errore durante la modifica della nota", { variant: "error" });
    }
  };

  const saveNote = async (note: Note) => {
    console.log("Updating note", note);
    // TODO: replace with actual API call
    enqueueSnackbar("Nota aggiornata", { variant: "info" });
  };

  const deleteNote = async (id: string) => {
    console.log("Deleting note", id);
    // TODO: replace with actual API call
    setNotes((prev) => prev.filter((n) => n.id !== id));
    enqueueSnackbar("Nota eliminata", { variant: "warning" });
    try {
      const response = await deleteClientNote(session.selectedBusiness.id, clientId, id);
      if (!response) {
        enqueueSnackbar("Errore durante l'eliminazione della nota", { variant: "error" });
        return;
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      enqueueSnackbar("Errore durante l'eliminazione della nota", { variant: "error" });
    }
  };

  const createNewNote = async () => {
    console.log("Creating note", newNote);

    // TODO: replace with actual API call
    const created: Note = {
      id: Date.now().toString(),
      title: newNote.title,
      description: newNote.description,
      createdAt: new Date().toISOString(),
      createdBy: "currentUserId",
    };
    // Simulate API call
    const response = await addClientNote(session.selectedBusiness.id, clientId, created);
    if (!response) {
      enqueueSnackbar("Errore durante la creazione della nota", { variant: "error" });
      return;
    }
    setNotes((prev) => [created, ...prev]);
    setNewNote({ title: "", description: "" });
    enqueueSnackbar("Nota creata", { variant: "success" });
  };

  // Calculate paginated notes
  const paginated = notes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(notes.length / PAGE_SIZE) || 1;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Note Cliente</DialogTitle>
      <DialogContent>
        {/* New Note Form */}
        <Box display="flex" flexDirection="column" gap={theme.spacing(2)} mb={theme.spacing(3)}>
          <Typography variant="h6">Aggiungi Nuova Nota</Typography>
          <TextField
            label="Titolo"
            value={newNote.title}
            onChange={(e) => setNewNote((prev) => ({ ...prev, title: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Descrizione"
            value={newNote.description}
            onChange={(e) => setNewNote((prev) => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            rows={3}
          />
          <Box textAlign="right">
            <Button
              variant="contained"
              onClick={createNewNote}
              disabled={!newNote.title || !newNote.description}
            >
              Aggiungi Nota
            </Button>
          </Box>
        </Box>

        {/* Existing Notes List */}
        {paginated.map((note) => (
          <Box
            key={note.id}
            display="flex"
            alignItems="center"
            gap={theme.spacing(1)}
            mb={theme.spacing(2)}
          >
            {/* Semantic Icon */}
            {getIconForNote(note)}

            {/* Editable Fields */}
            <Box flexGrow={1}>
              <TextField
                label="Titolo"
                value={note.title}
                onChange={handleEdit(note.id, "title")}
                onBlur={() => saveNote(note)}
                fullWidth
                size="small"
              />
              <TextField
                label="Descrizione"
                value={note.description}
                onChange={handleEdit(note.id, "description")}
                onBlur={() => saveNote(note)}
                fullWidth
                size="small"
                multiline
                rows={2}
                sx={{ mt: theme.spacing(1) }}
              />
            </Box>

            {/* Actions */}
            <IconButton onClick={() => saveNote(note)}>
              <SaveIcon />
            </IconButton>
            <IconButton onClick={() => deleteNote(note.id)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        {/* Pagination Controls */}
        <Box display="flex" justifyContent="center" mt={theme.spacing(2)}>
          <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientNotesModal;
